/**
 * `/admin/automation/workloads` (A7Y-3475) — Actions routing audit +
 * per-workflow cost. Wiring only: this component owns the fetch and renders
 * the presentational sections from `WorkloadsSections.tsx` in the ticket's
 * specified order. Loading/error states mirror `AddisStatusBoard`'s.
 */
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { api } from '@/lib/api';
import { fmtLocal, fmtUtc } from './shared';
import {
  BannerRule,
  FindingsTable,
  FreshnessChip,
  RoutingTable,
  UnreadableNote,
  WorkflowSummaryTable,
  WorkloadsLegend,
  WorkloadsVerdictBanner,
} from './WorkloadsSections';

const REFETCH_INTERVAL_MS = 60_000;

export default function WorkloadsPage() {
  const query = useQuery({
    queryKey: ['workloads-status'],
    queryFn: () => api.getWorkloads(),
    refetchInterval: REFETCH_INTERVAL_MS,
  });

  if (query.isError) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/40 p-3 text-sm text-red-700 dark:text-red-300">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        Workloads failed to load. Check the Worker logs for /admin/autobuild/workloads.
      </div>
    );
  }

  if (!query.data) {
    return (
      <div className="flex items-center gap-2 p-4 text-sm text-zinc-500">
        <RefreshCw className="h-3 w-3 animate-spin" />
        Reading workload report…
      </div>
    );
  }

  const status = query.data;

  return (
    <div className="space-y-4">
      <BannerRule>
        Workload &amp; routing audit ·{' '}
        <span title={fmtUtc(status.generatedAt)}>{fmtLocal(status.generatedAt)}</span>
      </BannerRule>

      <div className="flex items-start justify-between gap-3">
        <WorkloadsVerdictBanner status={status} />
        <FreshnessChip
          producedAt={status.producedAt}
          stale={status.stale}
          staleAfterHours={status.staleAfterHours}
        />
      </div>

      <RoutingTable routing={status.routing} failoverState={status.failoverState} />
      <FindingsTable findings={status.findings} />
      <WorkflowSummaryTable workflows={status.workflows} weeks={status.weeks} />
      <WorkloadsLegend poolEvidence={status.poolEvidence} />
      <UnreadableNote unreadable={status.unreadable} />
    </div>
  );
}
